'use client';

import { useMemo, useState } from 'react';
import { motion, useReducedMotion } from 'motion/react';
import { ChevronLeft, ChevronRight, Plus, Minus, X, Sparkles, ShoppingBag } from 'lucide-react';
import { Input, Textarea } from '@components/ui';
import { CartSuggestions } from './CartSuggestions';
import { computeUpsellSuggestions, itemsForWizardStep } from '@/lib/upsell-engine';
import {
  WIZARD_STEPS,
  WIZARD_STEP_LABELS,
  type CategoryKind,
  type OrderWizardSettings,
  type PublicMenuItem,
  type UpsellRule,
  type WizardStep,
} from '@/types/menu-sales';

type CategoryRef = { id: string; name: string; kind: CategoryKind };

type CartLine = {
  lineId: string;
  id: string;
  name: string;
  price: number;
  quantity: number;
  image?: string;
  notes?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  items: PublicMenuItem[];
  categories: CategoryRef[];
  upsellRules: UpsellRule[];
  wizardSettings: OrderWizardSettings;
  cart: CartLine[];
  customer: { name: string; phone: string; email: string; notes: string };
  onCustomerChange: (customer: Props['customer']) => void;
  onAddToCart: (item: PublicMenuItem) => void;
  onUpdateQty: (lineId: string, delta: number) => void;
  onRemoveFromCart: (lineId: string) => void;
  onSubmit: () => void;
  submitting: boolean;
  resolveImage: (item: PublicMenuItem) => string;
  dismissedRuleIds: Set<string>;
  onDismissRule: (ruleId: string) => void;
};

export function OrderWizard({
  open,
  onClose,
  items,
  categories,
  upsellRules,
  wizardSettings,
  cart,
  customer,
  onCustomerChange,
  onAddToCart,
  onUpdateQty,
  onRemoveFromCart,
  onSubmit,
  submitting,
  resolveImage,
  dismissedRuleIds,
  onDismissRule,
}: Props) {
  const reduce = useReducedMotion();
  const [stepIndex, setStepIndex] = useState(0);
  const step = WIZARD_STEPS[stepIndex] as WizardStep;

  const stepItems = useMemo(
    () => itemsForWizardStep(step, items, categories),
    [step, items, categories],
  );

  const cartIds = useMemo(() => cart.map((c) => c.id), [cart]);

  const lastMainInCart = useMemo(() => {
    const catKind = new Map(categories.map((c) => [c.id, c.kind]));
    for (let i = cart.length - 1; i >= 0; i -= 1) {
      const line = cart[i];
      const item = items.find((it) => it.id === line.id);
      if (item && catKind.get(item.category_id) === 'main') return item;
    }
    return undefined;
  }, [cart, items, categories]);

  const suggestions = useMemo(() => {
    if (step === 'details') return [];
    return computeUpsellSuggestions({
      rules: upsellRules,
      cartItemIds: cartIds,
      items,
      categories,
      context: 'wizard',
      triggerItem: step === 'side' || step === 'drink' || step === 'dessert' ? lastMainInCart : undefined,
      wizardStep: step,
      dismissedRuleIds,
      limit: 2,
    });
  }, [step, upsellRules, cartIds, items, categories, lastMainInCart, dismissedRuleIds]);

  const cartTotal = cart.reduce((s, i) => s + i.price * i.quantity, 0);
  const cartCount = cart.reduce((s, i) => s + i.quantity, 0);
  const isLastStep = step === 'details';

  const goNext = () => {
    if (stepIndex < WIZARD_STEPS.length - 1) setStepIndex((i) => i + 1);
  };

  const goBack = () => {
    if (stepIndex > 0) setStepIndex((i) => i - 1);
  };

  const skipStep = () => {
    if (wizardSettings.allow_skip) goNext();
  };

  if (!open) return null;

  return (
    <div className="order-wizard-overlay">
      <motion.div
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        className="order-wizard-backdrop"
        onClick={onClose}
      />
      <motion.div
        initial={reduce ? false : { y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="order-wizard-sheet"
      >
        <header className="order-wizard-header">
          <button type="button" onClick={onClose} className="order-wizard-close" aria-label="Fechar wizard">
            <X className="w-5 h-5" />
          </button>
          <div>
            <p className="order-wizard-kicker">Pedido guiado</p>
            <h2 className="order-wizard-title">{WIZARD_STEP_LABELS[step]}</h2>
          </div>
          <span className="order-wizard-step-count">{stepIndex + 1}/{WIZARD_STEPS.length}</span>
        </header>

        <div className="order-wizard-progress">
          {WIZARD_STEPS.map((s, i) => (
            <div
              key={s}
              className={`order-wizard-progress-dot ${i <= stepIndex ? 'is-active' : ''} ${i === stepIndex ? 'is-current' : ''}`}
            />
          ))}
        </div>

        {step !== 'details' && suggestions.length > 0 && (
          <div className="order-wizard-suggestions">
            <p className="order-wizard-suggest-label">
              <Sparkles className="w-4 h-4" /> Sugestoes para ti
            </p>
            <CartSuggestions
              suggestions={suggestions}
              onAdd={onAddToCart}
              onDismiss={onDismissRule}
              compact
            />
          </div>
        )}

        <div className="order-wizard-stage">
          <div className="order-wizard-body">
            {step !== 'details' ? (
              <div className="order-wizard-grid">
                {stepItems.length === 0 ? (
                  <p className="text-sm text-ink-500 col-span-full py-8 text-center">
                    Sem produtos nesta categoria. Podes continuar.
                  </p>
                ) : (
                  stepItems.map((item) => {
                    const qtyInCart = cart
                      .filter((c) => c.id === item.id)
                      .reduce((sum, c) => sum + c.quantity, 0);
                    const inCart = qtyInCart > 0;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => onAddToCart(item)}
                        className={`order-wizard-item ${inCart ? 'is-in-cart' : ''}`}
                      >
                        <img src={resolveImage(item)} alt="" className="order-wizard-item-img" />
                        <div className="order-wizard-item-body">
                          <span className="order-wizard-item-name">{item.name}</span>
                          <span className="order-wizard-item-price">{Number(item.price).toFixed(2)} €</span>
                        </div>
                        <span className="order-wizard-item-add">
                          {inCart ? (
                            <span className="order-wizard-item-qty">{qtyInCart}</span>
                          ) : (
                            <Plus className="w-4 h-4" />
                          )}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            ) : (
              <div className="order-wizard-details space-y-4">
                <Input
                  label="Nome"
                  required
                  value={customer.name}
                  onChange={(e) => onCustomerChange({ ...customer, name: e.target.value })}
                />
                <Input
                  label="Telefone"
                  required
                  value={customer.phone}
                  onChange={(e) => onCustomerChange({ ...customer, phone: e.target.value })}
                />
                <Input
                  label="Email (opcional)"
                  value={customer.email}
                  onChange={(e) => onCustomerChange({ ...customer, email: e.target.value })}
                />
                <Textarea
                  label="Notas"
                  value={customer.notes}
                  onChange={(e) => onCustomerChange({ ...customer, notes: e.target.value })}
                />
              </div>
            )}
          </div>

          <aside className="order-wizard-live-cart" aria-label="Carrinho do pedido guiado">
            <div className="order-wizard-live-cart-head">
              <ShoppingBag className="w-4 h-4 text-brand" aria-hidden />
              <span className="order-wizard-live-cart-title">O teu pedido</span>
              {cartCount > 0 && (
                <span className="order-wizard-live-cart-badge">{cartCount}</span>
              )}
            </div>
            <div className="order-wizard-live-cart-list">
              {cart.length === 0 ? (
                <p className="order-wizard-live-cart-empty">
                  Ainda vazio — escolhe produtos nos passos anteriores.
                </p>
              ) : (
                cart.map((line) => (
                  <div key={line.lineId} className="order-wizard-live-cart-line">
                    <div className="order-wizard-live-cart-line-info">
                      <span className="order-wizard-live-cart-line-name">{line.name}</span>
                      <span className="order-wizard-live-cart-line-price">
                        {(line.price * line.quantity).toFixed(2)} €
                      </span>
                    </div>
                    <div className="order-wizard-live-cart-line-actions">
                      <button
                        type="button"
                        onClick={() => onUpdateQty(line.lineId, -1)}
                        className="order-wizard-live-cart-btn"
                        aria-label={`Reduzir ${line.name}`}
                      >
                        <Minus className="w-3.5 h-3.5" />
                      </button>
                      <span className="order-wizard-live-cart-qty">{line.quantity}</span>
                      <button
                        type="button"
                        onClick={() => onUpdateQty(line.lineId, 1)}
                        className="order-wizard-live-cart-btn"
                        aria-label={`Aumentar ${line.name}`}
                      >
                        <Plus className="w-3.5 h-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemoveFromCart(line.lineId)}
                        className="order-wizard-live-cart-btn order-wizard-live-cart-btn-remove"
                        aria-label={`Remover ${line.name}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="order-wizard-live-cart-total">
              <span>Total</span>
              <span className="order-wizard-live-cart-total-value">{cartTotal.toFixed(2)} €</span>
            </div>
          </aside>
        </div>

        <footer className="order-wizard-footer">
          {stepIndex > 0 ? (
            <button type="button" onClick={goBack} className="order-wizard-btn-secondary">
              <ChevronLeft className="w-4 h-4" /> Anterior
            </button>
          ) : (
            <span />
          )}
          <div className="flex gap-2">
            {!isLastStep && wizardSettings.allow_skip && (
              <button type="button" onClick={skipStep} className="order-wizard-btn-secondary">
                Saltar
              </button>
            )}
            {isLastStep ? (
              <button
                type="button"
                disabled={!customer.name || !customer.phone || cart.length === 0 || submitting}
                onClick={onSubmit}
                className="btn-gradient"
              >
                {submitting ? 'A enviar...' : 'Confirmar pedido'}
              </button>
            ) : (
              <button type="button" onClick={goNext} className="btn-gradient">
                Continuar <ChevronRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </footer>
      </motion.div>
    </div>
  );
}
